/**
 * @author junye.he
 * @date 2021.10.08
 *
 * 几何变换函数集合
 */

// 坐标集合数组，如 [1, 1] 代表坐标 x: 1, y: 1
export type CoordinateSet = [x: number, y: number]

/**
 * 平移函数
 * @param point 平移之前的坐标
 * @param dx x 轴的偏移值
 * @param dy y 轴的偏移值
 * @return point 平移之后的坐标
 */
export function translateShape(
  point: CoordinateSet,
  dx: number,
  dy: number,
): CoordinateSet {
  const preX = point[0]
  const preY = point[1]
  const curX = preX + dx
  const curY = preY + dy
  return [curX, curY]
}

/**
 * 旋转函数
 * @param point 旋转之前坐标
 * @param pivot 旋转枢轴坐标
 * @param angle 旋转弧度
 * @return point 旋转之后的坐标
 */
export function rotateShape(
  point: CoordinateSet,
  pivot: CoordinateSet,
  angle: number,
): CoordinateSet {
  const sin = Math.sin(angle)
  const cos = Math.cos(angle)
  const preX = point[0]
  const preY = point[1]
  const cenX = pivot[0]
  const cenY = pivot[1]
  const curX = cos * (preX - cenX) - sin * (preY - cenY) + cenX
  const curY = cos * (preY - cenY) + sin * (preX - cenX) + cenY
  return [curX, curY]
}

/**
 * 缩放函数
 * @param point 缩放之前的坐标
 * @param pivot 缩放枢轴坐标
 * @param scale 缩放 x/y 的系数
 * @return point 缩放后的坐标
 */
export function scaleShape(
  point: CoordinateSet,
  pivot: CoordinateSet,
  scale: CoordinateSet,
): CoordinateSet {
  const preX = point[0]
  const preY = point[1]
  const cenX = pivot[0]
  const cenY = pivot[1]
  const scaleX = scale[0]
  const scaleY = scale[1]
  const deltaX = (preX - cenX) * scaleX
  const deltaY = (preY - cenY) * scaleY
  const curX = cenX + deltaX
  const curY = cenY + deltaY
  return [curX, curY]
}

/**
 * 获取多节点的中心点
 * @param pointArray
 */
export function getMultiNodePivot(pointArray: CoordinateSet[]): CoordinateSet {
  const pivotX: CoordinateSet = [Infinity, -Infinity]
  const pivotY: CoordinateSet = [Infinity, -Infinity]
  pointArray.forEach((point: CoordinateSet) => {
    if (point[0] < pivotX[0]) {
      pivotX[0] = point[0]
    }
    if (point[0] > pivotX[1]) {
      pivotX[1] = point[0]
    }
    if (point[1] < pivotY[0]) {
      pivotY[0] = point[1]
    }
    if (point[1] > pivotY[1]) {
      pivotY[1] = point[1]
    }
  })
  return [(pivotX[0] + pivotX[1]) / 2, (pivotY[0] + pivotY[1]) / 2]
}
